import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  Bug,
  Search,
  RefreshCw,
  Trash2,
  ChevronDown,
  ChevronRight,
  Shield,
  Clock
} from 'lucide-react';
import { useToast } from '../components/Toast';

interface LogEntry {
  id: number;
  type: string;
  message: string;
  context: Record<string, any>;
  created_at: string;
}

interface LogsResponse {
  total: number;
  limit: number;
  offset: number;
  logs: LogEntry[];
}

interface LogSummary {
  by_type: Array<{ type: string; count: number; last_occurrence: string }>;
  recent_errors_24h: number;
}

const LOG_LEVELS = ['emergency', 'alert', 'critical', 'error', 'warning', 'notice', 'info', 'debug'];

const LEVEL_COLORS: Record<string, { bg: string; text: string; icon: any }> = {
  emergency: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle },
  alert: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle },
  critical: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertCircle },
  error: { bg: 'bg-red-50', text: 'text-red-700', icon: AlertCircle },
  warning: { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: AlertTriangle },
  notice: { bg: 'bg-blue-50', text: 'text-blue-700', icon: Info },
  info: { bg: 'bg-gray-50', text: 'text-gray-700', icon: Info },
  debug: { bg: 'bg-gray-50', text: 'text-gray-500', icon: Bug },
};

export default function Logs() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  
  const [filter, setFilter] = useState({
    level: '',
    search: '',
    limit: 50
  });
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<'async' | 'boot' | 'syscall' | 'security'>('async');

  // Fetch logs
  const { data: logs, isLoading, refetch } = useQuery<LogsResponse>({
    queryKey: ['kernel-logs', filter, activeTab],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter.level) params.append('level', filter.level);
      if (filter.search) params.append('search', filter.search);
      params.append('limit', filter.limit.toString());
      
      let endpoint = '/api/v1/kernel/logs';
      if (activeTab === 'boot') endpoint = '/api/v1/kernel/boot-log';
      if (activeTab === 'syscall') endpoint = '/api/v1/kernel/syscalls';
      if (activeTab === 'security') endpoint = '/api/v1/kernel/logs/security';
      
      const response = await fetch(`${endpoint}?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch logs');
      return response.json();
    },
    refetchInterval: 10000
  });

  // Fetch summary
  const { data: summary } = useQuery<LogSummary>({
    queryKey: ['kernel-logs-summary'],
    queryFn: async () => {
      const response = await fetch('/api/v1/kernel/logs/summary', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch summary');
      return response.json();
    },
    refetchInterval: 30000
  });

  // Clear old logs mutation
  const clearLogsMutation = useMutation({
    mutationFn: async (days: number) => {
      const response = await fetch('/api/v1/kernel/logs', {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ older_than_days: days })
      });
      if (!response.ok) throw new Error('Failed to clear logs');
      return response.json();
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['kernel-logs'] });
      queryClient.invalidateQueries({ queryKey: ['kernel-logs-summary'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to clear logs: ${error.message}`);
    }
  });

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedLogs(newExpanded);
  };

  const getLevelStyle = (type: string) => {
    return LEVEL_COLORS[type] || LEVEL_COLORS.info;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kernel Logs</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage kernel log entries
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => {
              if (confirm('Delete logs older than 30 days?')) {
                clearLogsMutation.mutate(30);
              }
            }}
            disabled={clearLogsMutation.isPending}
            className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Old
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <div className="bg-white overflow-hidden shadow rounded-lg p-4">
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-gray-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Logs</p>
              <p className="text-2xl font-semibold text-gray-900">{logs?.total || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-8 w-8 text-red-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Errors (24h)</p>
              <p className="text-2xl font-semibold text-red-600">{summary?.recent_errors_24h || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-blue-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Log Types</p>
              <p className="text-2xl font-semibold text-gray-900">{summary?.by_type?.length || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg p-4">
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-purple-400" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Security Events</p>
              <p className="text-2xl font-semibold text-gray-900">
                {summary?.by_type?.find(t => t.type === 'security')?.count || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'async', label: 'Application Logs', icon: FileText },
            { id: 'boot', label: 'Boot Logs', icon: Clock },
            { id: 'syscall', label: 'Syscall Logs', icon: Bug },
            { id: 'security', label: 'Security Logs', icon: Shield },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      {activeTab === 'async' && (
        <div className="bg-white shadow rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
              <select
                value={filter.level}
                onChange={(e) => setFilter({ ...filter, level: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">All Levels</option>
                {LOG_LEVELS.map(level => (
                  <option key={level} value={level}>{level.charAt(0).toUpperCase() + level.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filter.search}
                  onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                  placeholder="Search logs..."
                  className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Limit</label>
              <select
                value={filter.limit}
                onChange={(e) => setFilter({ ...filter, limit: parseInt(e.target.value) })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {(logs?.logs || []).length > 0 ? (
              (logs?.logs || []).map((log: any) => {
                const style = getLevelStyle(log.type || log.status || 'info');
                const Icon = style.icon;
                const isExpanded = expandedLogs.has(log.id);
                const hasContext = log.context && Object.keys(log.context).length > 0;
                
                return (
                  <div key={log.id} className={`${style.bg}`}>
                    <div 
                      className="px-4 py-3 cursor-pointer hover:bg-opacity-75"
                      onClick={() => hasContext && toggleExpand(log.id)}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-0.5">
                          {hasContext ? (
                            isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Icon className={`h-4 w-4 ${style.text}`} />
                          )}
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
                              {(log.type || log.status || log.syscall_name || 'log').toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(log.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-900 break-words">
                            {log.message || log.phase_name || log.syscall_name || 'No message'}
                          </p>
                          {log.error_message && (
                            <p className="mt-1 text-sm text-red-600">{log.error_message}</p>
                          )}
                          {log.execution_time && (
                            <p className="mt-1 text-xs text-gray-500">
                              Execution: {log.execution_time}ms | Memory: {log.memory_delta} bytes
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    {isExpanded && hasContext && (
                      <div className="px-4 pb-3 ml-7">
                        <pre className="text-xs bg-gray-800 text-gray-100 p-3 rounded overflow-x-auto">
                          {JSON.stringify(log.context, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-12 text-center text-gray-500">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No logs found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filter.search || filter.level ? 'Try adjusting your filters' : 'Logs will appear here as they are generated'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Type Summary */}
      {summary?.by_type && summary.by_type.length > 0 && (
        <div className="mt-6 bg-white shadow rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Log Distribution</h3>
          <div className="flex flex-wrap gap-2">
            {summary.by_type.map((item) => {
              const style = getLevelStyle(item.type);
              return (
                <button
                  key={item.type}
                  onClick={() => setFilter({ ...filter, level: item.type })}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${style.bg} ${style.text} hover:opacity-75`}
                >
                  {item.type}: {item.count}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
